import type { AdError, AdInfo } from '@softgames/ad-flow';
import type { AppState } from '../state/AppState';
import type { ReviveReason } from '../../game/GameConstants';
import type {
  MetaProperties,
  ResourceActionTypeValueType,
  SeasonPassStatusContextValueType,
  PaymentProviderValueType,
} from './trackingModules/avo/Avo';
import type { IAPPlacementData, IAPProduct } from '../iap/types';
import type { Purchase } from '../platform/Platform';
import type { ResourceMap } from '../resources/types';
import type { AppStateController } from '../state/AppStateController';

export enum OfferInteractionLocation {
  ShopView = 'ShopView',
  LevelFail = 'LevelFail',
  MenuIcon = 'MenuIcon',
  OfferPopup = 'OfferPopup',
}

export interface MetaPropertiesGetter {
  getMetaProperties(properties?: MetaProperties[]): MetaProperties[] | null;
}

export interface TrackingModule {
  addMetaPropertiesGetter(getter: MetaPropertiesGetter): void;
  trackGameInitialized(
    data: AppState,
    props: {
      userId: string;
      entryPointName: string | null;
      entryPointData: unknown;
    },
  ): void;
  trackLevelStarted(properties: { levelNumber: number; lives: number; tileRvAmount: number }): void;
  trackLevelWon(properties: {
    levelNumber: number;
    levelId: string;
    levelAttemptId: string;
    lives: number;
    doubleRewardShown: boolean;
    inventory: ResourceMap;
    metaProperties?: MetaProperties[];
  }): void;
  trackLevelLost(properties: { levelNumber: number; lives: number; metaProperties?: MetaProperties[] }): void;
  trackReviveShown(properties: { levelNumber: number }): void;
  trackReviveUsed(properties: { levelNumber: number; reason: ReviveReason; resourceAmount: number }): void;
  trackDioramaCompleted(dioramaIndex: number): void;
  trackAdFinished(properties: { adInfo: AdInfo; adError: AdError | null; metaProperties?: MetaProperties[] }): void;
  splitTest(testSlug: string, distribution: number, metaProperties?: MetaProperties[]): void;
  trackResourceAction(properties: {
    levelId: string;
    levelNumber: number;
    levelAttemptId: string;
    actionTrigger: string;
    actionType: ResourceActionTypeValueType;
    resourceUpdate: ResourceMap;
    inventory: ResourceMap;
    gameEventId?: string;
    transactionId?: string;
    metaProperties?: MetaProperties[];
  }): void;
  trackEventStarted(type: string, eventId: string): void;
  trackEventFinished(properties: {
    type: string;
    eventId: string;
    progress: number;
    rank?: number;
    metaProperties?: MetaProperties[];
  }): void;
  trackPurchase(properties: {
    data: AppState;
    product: IAPProduct;
    purchase: Purchase;
    placementData: IAPPlacementData;
    offerImpressionId: string | null;
    paymentProvider: PaymentProviderValueType;
  }): void;
  trackRvTileUnlock(properties: { levelAttemptId: string; trigger: 'ad' | 'coin' }): void;
  trackOfferShown(properties: {
    product: { id: string; variantId: string; activationId: string; platformId: string };
    location: OfferInteractionLocation;
    impressionId: string;
  }): void;
  trackOfferClick(properties: {
    product: { id: string; variantId: string; activationId: string; platformId: string };
    location: OfferInteractionLocation;
    impressionId: string;
  }): void;
  trackUIInteraction(properties: {
    elementId: string;
    contextName: string;
    action: 'View' | 'Click';
    metaProperties?: MetaProperties[];
  }): void;
  trackSeasonPass(properties: {
    points: number;
    pointsIncrease: number;
    seasonPassId: string;
    eventContext: SeasonPassStatusContextValueType;
    productId?: string;
    metaProperties?: MetaProperties[];
  }): void;
  trackPlayerAttribution(properties: {
    attributionPayload: string;
    attributionNetwork?: string;
    attributionCampaign?: string;
    metaProperties?: MetaProperties[];
  }): void;
  trackNativeAppLaunched(properties: {
    appBuildNumber: number;
    appVersion: string;
    memoryTotal?: number;
    memoryAvailable?: number;
    assetQuality?: 'low' | 'high';
    deviceIdentifier: string;
    metaProperties?: MetaProperties[];
  }): void;
  trackCollectionEventEnd(properties: {
    gameEventId: string;
    gameEventTier: number;
    metaProperties?: MetaProperties[];
  }): void;
  trackCollectionEventTokenUpdate(properties: {
    gameEventId: string;
    tokenAmount: number;
    levelAttemptId: string;
    metaProperties?: MetaProperties[];
  }): void;
  trackCheatsEnabled(): void;
}

export type TrackingModuleFactory = (deps: {
  stateController: AppStateController;
}) => TrackingModule;
